import { AxiosError } from 'axios'
import { useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMatch } from 'react-router'
import { ErrorsContext } from '../contexts/errors_context'
import { cardBreadcrumbs } from '../lib/internal_links'
import { applyDocumentMeta, cardPageMetaFromItem } from '../lib/seo_meta'
import { itemsRepository } from '../repositories/items_repository'

import '../stylesheets/Sets.scss'
import { cardPageJsonLd, localizedName } from '../lib/json_ld'
import { Item, Unit } from '../types'
import { cardTitle, marketViewFromUnits, metaDescription } from '../seo/card_landing'
import { priorityCardByItemId } from '../seo/priority_cards'
import { Breadcrumbs } from './breadcrumbs'
import { CardCatalogFacts } from './card_catalog_facts'
import { CardContextLinks } from './card_context_links'
import { CardSingle } from './card_single'
import { GenericNotFound } from './generic_not_found'
import { HighIntentCardLanding } from './high_intent_card_landing'
import { JsonLd } from './json_ld'
import { ListingSingle } from './listing_single'

const cardSettingsItem = {
  conditionSelection: true,
  canCollect: true,
  imageResolution: '2x',
}

const Items = (): JSX.Element => {
  const { i18n, t } = useTranslation()

  const { addError } = useContext(ErrorsContext)
  const [loading, setLoading] = useState<boolean>(true)
  const [item, setItem] = useState<Item>()
  const [listingsLoading, setListingsLoading] = useState<boolean>(false)
  const [listingState, setListingState] = useState<{ itemId: number, units: Unit[] }>()

  const params = useMatch('/categories/:category_id/items/:item_id')?.params

  // const categoryId = useMemo<number|undefined>(() => {
  //   const id = parseInt(params?.category_id ?? '')
  //   return isNaN(id) ? undefined : id
  // }, [params])

  const itemId = useMemo<number|undefined>(() => {
    const id = parseInt(params?.item_id ?? '')
    return isNaN(id) ? undefined : id
  }, [params])

  useEffect(() => {
    if (!itemId) {
      setLoading(false)
      return
    }

    let active = true
    setLoading(true)

    itemsRepository
      .get(itemId)
      .then(({ item: next }) => {
        if (!active) return
        setItem(next)
        setListingsLoading(true)
      })
      .catch((err: AxiosError) => {
        if (!active) return
        addError?.(err)
      })
      .finally(() => {
        if (active) setLoading(false)
      })

    return () => {
      active = false
    }
  }, [addError, itemId])

  useEffect(() => {
    if (!item) return

    const requestedId = item.id
    let active = true
    setListingsLoading(true)

    itemsRepository
      .units(requestedId, '')
      .then(({ listings }) => {
        if (!active) return
        setListingState({ itemId: requestedId, units: listings })
      })
      .catch((err: AxiosError) => {
        if (!active) return
        addError?.(err)
      })
      .finally(() => {
        if (active) setListingsLoading(false)
      })

    return () => {
      active = false
    }
  }, [addError, item])

  const units = listingState && item && listingState.itemId === item.id ? listingState.units : undefined

  const crumbs = useMemo(() => {
    if (!item) return []
    return cardBreadcrumbs(item, i18n.language, t('links.home'))
  }, [i18n.language, item, t])

  const itemName = useMemo(() => {
    if (!item) return

    return localizedName(item, i18n.language)
  }, [i18n.language, item])

  const routeCategoryId = useMemo(() => {
    const id = parseInt(params?.category_id ?? '')
    return isNaN(id) ? undefined : id
  }, [params?.category_id])

  const jsonLd = useMemo(() => {
    if (!item || !itemName) return null

    return cardPageJsonLd({
      language: i18n.language,
      item,
      categoryId: routeCategoryId,
      units,
      conditionLabel: (condition: number) => t(`collections.conditions.long.${condition}`),
    })
  }, [i18n.language, item, itemName, routeCategoryId, t, units])

  const priorityCard = item ? priorityCardByItemId(item.id) : undefined

  useEffect(() => {
    if (!item) return

    if (priorityCard) {
      const market = marketViewFromUnits(units, listingsLoading)
      applyDocumentMeta({
        title: cardTitle(priorityCard, market.kind === 'asks'),
        description: metaDescription(priorityCard, market),
      })
      return
    }

    applyDocumentMeta(cardPageMetaFromItem(item))
  }, [item, listingsLoading, priorityCard, units])

  const listings = useMemo(() => {
    if (!item) return

    if (listingsLoading) {
      return <p>{ t('cards.marketplace.loading') }</p>
    }

    if (!units || units.length === 0) {
      return (
        <>
          <p>{ t('cards.marketplace.none.body1') }</p>
          <p><strong>{ t('cards.marketplace.none.body2', { name: itemName }) }</strong></p>
          <p>{ t('cards.marketplace.none.body3') }</p>
        </>
      )
    }

    return (
      <ul className="listings">
        {
          units.map((ui, i) => {
            return <ListingSingle key={ i } editing={ false } items={ [item] } unit={ ui } />
          })
        }
      </ul>
    )
  }, [item, itemName, listingsLoading, t, units])

  if (loading) {
    return <p>{ t('cards.marketplace.loading') }</p>
  }

  if (!item) return <GenericNotFound />

  return (
    <div>
      <JsonLd data={ jsonLd } />
      <Breadcrumbs crumbs={ crumbs } label={ t('links.breadcrumb') } />
      <h2>{ priorityCard ? priorityCard.h1 : itemName }</h2>
      {
        priorityCard
          ? <HighIntentCardLanding card={ priorityCard } units={ units } listingsLoading={ listingsLoading } />
          : null
      }
      <div>
        <ul className="cards-list test">
          <CardSingle key={ item.id } item={ item } cardSettings={ cardSettingsItem } />
        </ul>
      </div>
      <CardCatalogFacts item={ item } />
      <hr />
      <div id='listings'>
        <h2>{ t('cards.marketplace.title') }</h2>
        { listings }
      </div>
      <CardContextLinks item={ item } units={ units ?? [] } />
    </div>
  )
}

export { Items }
